 import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth, db, checkUsernameAvailable, claimUsername, isValidUsernameFormat } from '../../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate, Link } from 'react-router-dom';
import background from '../../assets/Background_Test.jpg';

const PATREON_AUTH_URL = 'https://us-central1-mygmtracker.cloudfunctions.net/patreonAuth';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundImage: 'url(' + background + ')',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Monda', sans-serif",
    position: 'relative',
  },
  overlay: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.75) 100%)',
    pointerEvents: 'none',
  },
  title: {
    fontFamily: "'Anton', sans-serif",
    fontSize: '28px', letterSpacing: '3px',
    color: '#fff', textAlign: 'center', marginBottom: '8px',
    textShadow: '0 0 30px rgba(200,0,0,0.4)',
  },
  card: {
    position: 'relative', zIndex: 2,
    background: 'rgba(10,10,10,0.82)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px', padding: '40px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 8px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05)',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  label: { color: 'rgba(255,255,255,0.5)', fontSize: '11px', letterSpacing: '1px', fontFamily: "'Monda', sans-serif", marginBottom: '4px' },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '4px', padding: '10px 12px',
    color: '#fff', fontSize: '14px', outline: 'none',
    fontFamily: "'Monda', sans-serif", transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  btn: {
    fontFamily: "'Monda', sans-serif", letterSpacing: '1px',
    background: 'transparent', border: '1px solid rgba(255,255,255,0.5)',
    color: '#fff', padding: '10px', borderRadius: '4px',
    cursor: 'pointer', fontSize: '12px', transition: 'background 0.2s, border-color 0.2s, color 0.2s', width: '100%',
  },
  btnPatreon: {
    fontFamily: "'Monda', sans-serif", letterSpacing: '1px',
    background: '#fff', border: '1px solid #fff',
    color: '#052d49', padding: '10px', borderRadius: '4px',
    cursor: 'pointer', fontSize: '12px', transition: 'background 0.2s, border-color 0.2s, color 0.2s', width: '100%',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
  },
  divider: {
    display: 'flex', alignItems: 'center', gap: '10px',
    color: 'rgba(255,255,255,0.2)', fontSize: '11px',
    fontFamily: "'Monda', sans-serif",
  },
  dividerLine: {
    flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)',
  },
  error: { color: '#ff4444', fontSize: '12px', fontFamily: "'Monda', sans-serif" },
  link: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center', fontFamily: "'Monda', sans-serif" },
};

// ── Statut du pseudo (vérif live) ────────────────────────────────────
const USERNAME_STATUS_STYLES = {
  checking: { color: 'rgba(255,255,255,0.4)', text: 'Vérification...' },
  available: { color: '#2ecc71', text: 'Pseudo disponible' },
  taken: { color: '#ff4444', text: 'Ce pseudo est déjà pris' },
  invalid: { color: '#ff4444', text: '3-20 caractères : lettres, chiffres, _' },
};

export default function Register() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [hovered, setHovered] = useState(false);
  const [hoveredPatreon, setHoveredPatreon] = useState(false);
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState('idle'); // idle|checking|available|taken|invalid
  const navigate = useNavigate();

  // ── Vérif de dispo en direct (debounce 500ms) ──────────────────────
  useEffect(() => {
    if (!username) { setUsernameStatus('idle'); return; }
    if (!isValidUsernameFormat(username)) { setUsernameStatus('invalid'); return; }
    setUsernameStatus('checking');
    const t = setTimeout(async () => {
      try {
        const ok = await checkUsernameAvailable(username);
        setUsernameStatus(ok ? 'available' : 'taken');
      } catch {
        setUsernameStatus('idle');
      }
    }, 500);
    return () => clearTimeout(t);
  }, [username]);

  // ── Création du compte + claim atomique du pseudo ──────────────────
  // Si le claim échoue (pseudo pris entre-temps), on supprime le compte
  // Auth qu'on vient de créer pour ne pas laisser un compte "orphelin".
  const createAccountWithUsername = async (cred) => {
    try {
      await claimUsername(cred.user.uid, username);
      await updateProfile(cred.user, { displayName: username });
      await setDoc(doc(db, 'users', cred.user.uid), {
        email,
        createdAt: new Date(),
        role: 'free',
        patreonStatus: { active: false },
      }, { merge: true });
    } catch (claimErr) {
      await cred.user.delete().catch(() => {});
      if (claimErr.message === 'USERNAME_TAKEN') {
        throw new Error('This username was just taken, please choose another one.');
      }
      throw new Error('Erreur lors de la création du compte : ' + claimErr.message);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidUsernameFormat(username)) {
      setError('Invalid username (3-20 characters: letters, numbers, _).');
      return;
    }
    setLoading(true);
    try {
      const available = await checkUsernameAvailable(username);
      if (!available) {
        setError('This username is already taken.');
        setLoading(false);
        return;
      }
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await createAccountWithUsername(cred);
      // Redirection propre vers l'accès restreint
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError('This email is already in use.');
      else if (err.code === 'auth/weak-password') setError('Password too short (6 characters min).');
      else setError(err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // Inscription + liaison Patreon en une seule etape
  const handleRegisterWithPatreon = async () => {
    if (!username || !email || !password) {
      setError('Fill in all fields before continuing with Patreon.');
      return;
    }
    if (!isValidUsernameFormat(username)) {
      setError('Invalid username (3-20 characters: letters, numbers, _).');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const available = await checkUsernameAvailable(username);
      if (!available) {
        setError('This username is already taken.');
        setLoading(false);
        return;
      }
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await createAccountWithUsername(cred);
      // Rediriger directement vers OAuth Patreon
      window.location.href = PATREON_AUTH_URL + '?state=' + cred.user.uid;
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') setError('This email is already in use.');
      else if (err.code === 'auth/weak-password') setError('Password too short (6 characters min).');
      else setError(err.message || 'Something went wrong.');
      setLoading(false);
    }
  };

  const canSubmit = usernameStatus !== 'taken' && usernameStatus !== 'invalid' && usernameStatus !== 'checking';

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Anton&family=Imprima&family=Monda:wght@400;700&display=swap" rel="stylesheet" />
      <div style={styles.page}>
        <div style={styles.overlay} />
        <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '420px', padding: '0 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img
            src={process.env.PUBLIC_URL + '/logo-main.png'}
            alt="Logo"
            style={{ width: '160px', height: '160px', objectFit: 'contain', filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.15))', marginBottom: '8px' }}
            onError={function(e) { e.target.style.display = 'none'; }}
          />
          <div style={styles.title}>SIGNUP</div>
          <div style={styles.card}>
            <div>
              <div style={styles.label}>USERNAME</div>
              <input style={styles.input} type="text" value={username}
                onChange={function(e) { setUsername(e.target.value); }}
                onFocus={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                onBlur={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
              {usernameStatus !== 'idle' && (
                <div style={{ fontSize: '11px', marginTop: '4px', fontFamily: "'Monda', sans-serif", color: USERNAME_STATUS_STYLES[usernameStatus].color }}>
                  {USERNAME_STATUS_STYLES[usernameStatus].text}
                </div>
              )}
            </div>
            <div>
              <div style={styles.label}>EMAIL</div>
              <input style={styles.input} type="email" value={email}
                onChange={function(e) { setEmail(e.target.value); }}
                onFocus={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                onBlur={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
            </div>
            <div>
              <div style={styles.label}>PASSWORD</div>
              <input style={styles.input} type="password" value={password}
                onChange={function(e) { setPassword(e.target.value); }}
                onFocus={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                onBlur={function(e) { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <button
              style={{ ...styles.btn, background: hovered ? 'rgba(255,255,255,0.1)' : 'transparent', borderColor: hovered ? '#fff' : 'rgba(255,255,255,0.5)', opacity: (loading || !canSubmit) ? 0.6 : 1 }}
              onMouseEnter={function() { setHovered(true); }}
              onMouseLeave={function() { setHovered(false); }}
              onClick={handleRegister}
              disabled={loading || !canSubmit}>
              {loading ? 'CREATION...' : 'CREATE ACCOUNT'}
            </button>

            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              OU
              <div style={styles.dividerLine} />
            </div>

            <button
              style={{ ...styles.btnPatreon, background: hoveredPatreon ? '#f0f0f0' : '#fff', borderColor: '#fff', opacity: (loading || !canSubmit) ? 0.6 : 1 }}
              onMouseEnter={function() { setHoveredPatreon(true); }}
              onMouseLeave={function() { setHoveredPatreon(false); }}
              onClick={handleRegisterWithPatreon}
              disabled={loading || !canSubmit}>
              <svg width="18" height="18" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd" stroke-linejoin="round" stroke-miterlimit="2">
                <g transform="matrix(.47407 0 0 .47407 .383 .422)">
                  <path d="M1033.05 324.45c-.19-137.9-107.59-250.92-233.6-291.7-156.48-50.64-362.86-43.3-512.28 27.2-181.1 85.46-237.99 272.66-240.11 459.36-1.74 153.5 13.58 557.79 241.62 560.67 169.44 2.15 194.67-216.18 273.07-321.33 55.78-74.81 127.6-95.94 216.01-117.82 151.95-37.61 255.51-157.53 255.29-316.38z" fill="#000000" fill-rule="nonzero"/>
                </g>
              </svg>
              SIGNUP + LINK PATREON
            </button>

            <div style={styles.link}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: 'rgba(255,255,255,0.7)' }}>Login</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
